import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '../app/hooks';
import SubSection, { SubSectionProps, SubSectionTitle } from './SubSection';
import { OrderingSelectors, OrderingConstants, OrderingOperations } from 'polygon-ordering';
import moment from 'moment';
import { DELIVERY_SALE_TYPES, SALE_TYPE } from '../libs/polygon-ordering/src/constants/saleType';
import RadioCheck from './RadioCheck';
import Text from './Text';
import getThemeLookup from '../selectors/getThemeLookup';
import { validateFutureMenuItems } from '../thunks/validateFutureMenuItems';
import { Skeleton } from '@mui/material';
import { TEXT_PROPERTIES } from '../utils/theme';
import FutureDateSelector from './FutureDateSelector';
import SectionContainer from './SectionContainer';
import SectionHeading from './SectionHeading';
import SubSectionChevron from './SubSectionChevron';
import { DELIVERY_DETAILS_MODAL_ID } from '../modals/DeliveryDetailsModal';
import { setCurrentModal } from '../thunks/setCurrentModal';
import { logEvent } from '../utils/analytics';
import EVENTS from '../constants/events';
import CurrentDateSelector from './CurrentDateSelector';

const { setBufferPickupTime, fetchOrderingProviderDetails } = OrderingOperations;
const { ASAP_TIME } = OrderingConstants;
const {
  getSaleType,
  getPickupTime,
  getDeliveryTime,
  getDeliveryTimeFetchedEstimate,
  getBypassDeliveryDetails,
  getLocation,
  getDeliveryTimeFetchedEstimateSyncing,
  getFutureOrderingLimitBySaleType,
} = OrderingSelectors;

const DeliveryTimeASAPEstimateTime = () => {
  const { t } = useTranslation();
  const p = useAppSelector(getThemeLookup);

  return (
    <Text style={p('modalSubSection', TEXT_PROPERTIES)}>
      {t('asap')}:
      <Skeleton
        variant="text"
        sx={{
          fontSize: '1rem',
          width: 80,
          display: 'inline-block',
          marginLeft: '0.5em',
        }}
      />
    </Text>
  );
};

/**
 * TimeSummary
 *
 * This appears in the checkout screen.
 *
 * It gets conditionally hidden or shown based on the saletype in OrderSummary.
 * This isn't shown for table ordering or dining in, but for other saletypes
 * where it requires theuser to select a time this does appear.
 */
const TimeSummary = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const location = useAppSelector(getLocation) as POSLocation;
  //const p = useAppSelector(getThemeLookup);
  const saleType = useAppSelector(getSaleType);
  const pickupTime = useAppSelector(getPickupTime);
  const deliveryTime = useAppSelector(getDeliveryTime);
  const deliveryTimeFetchedEstimate = useAppSelector(getDeliveryTimeFetchedEstimate);
  const deliveryTimeFetchedEstimateSyncing = useAppSelector(getDeliveryTimeFetchedEstimateSyncing);

  const [optionsCollapsed, setOptionsCollapsed] = useState(false);
  const bypassDeliveryDetails = useAppSelector(getBypassDeliveryDetails);

  const isDelivery = DELIVERY_SALE_TYPES.includes(saleType);

  const effectiveDeliveryAddress = useAppSelector(state => state.desiredDeliveryAddress);

  const deliveryAddress = [
    effectiveDeliveryAddress?.buildingInformation,
    effectiveDeliveryAddress?.formattedAddress,
  ]
    .filter(Boolean)
    .join(', ');

  const deliveryTimeUnvailable = isDelivery && deliveryTime == 'Invalid date' && !deliveryAddress;

  // Getting location opening and closing time based on current sale type
  const { openingTime: LocationOT, closingTime: LocationCT } =
    location.availableSaleTypesToday[String(saleType)];

  const beforeOrdering = moment().isBefore(moment(LocationOT));
  const afterOrdering = moment().isAfter(LocationCT);
  const afterHoursOrdering = beforeOrdering || afterOrdering;

  const isPickupOrTakeAway =
    saleType === SALE_TYPE.PICKUP ||
    saleType === SALE_TYPE.TAKE_AWAY ||
    saleType === SALE_TYPE.WEB_ORDERING;

  const deliveryASAPEstimate = `${t('asap')}: ~${moment(deliveryTimeFetchedEstimate).format(
    'h:mm a',
  )}`;

  const futureOrderingLimit = useAppSelector(getFutureOrderingLimitBySaleType({ saleType }));
  const saleTypeHasFutureOrdering = futureOrderingLimit > 0;

  const isDeliveryTimeASAP = !deliveryTime || deliveryTime === ASAP_TIME;

  const renderDeliveryTimeASAPEstimateTimeSkeleton = useCallback(
    () => <DeliveryTimeASAPEstimateTime />,
    [],
  );

  const [subSectionTitle, setSubSectionTitle] = useState<SubSectionTitle>('');

  const pickupOrTakeawayTimeIsAsap = !pickupTime || pickupTime === ASAP_TIME;

  const updateSubSectionTitle = useCallback(() => {
    let _subSectionTitle: SubSectionTitle = 'Time unavailable';

    if (isPickupOrTakeAway) {
      if (pickupOrTakeawayTimeIsAsap) {
        _subSectionTitle = `${t('')} ${
          afterHoursOrdering
            ? moment(LocationOT)
                .add(location.averageOrderWaitTime, 'minutes')
                .add(beforeOrdering ? 0 : 1, 'day')
                .format(beforeOrdering ? 'h:mm a' : ' h:mm a dddd D MMM  YYYY')
            : location.averageOrderWaitTime + ' mins'
        }`;
      } else {
        _subSectionTitle = moment(pickupTime).format('h:mm a dddd D MMM  YYYY');
      }
    }

    if (isDelivery) {
      if (isDeliveryTimeASAP) {
        if (deliveryTimeFetchedEstimateSyncing) {
          _subSectionTitle = renderDeliveryTimeASAPEstimateTimeSkeleton;
        } else {
          _subSectionTitle = deliveryASAPEstimate;
        }
      } else if (deliveryTime !== 'Invalid date') {
        _subSectionTitle = moment(deliveryTime).format(
          saleTypeHasFutureOrdering ? 'ddd, D MMM, YY h:mm a' : 'h:mm a',
        );
      } else if (deliveryAddress) {
        _subSectionTitle = deliveryAddress;
      }
    }

    if (saleType === SALE_TYPE.CATERING) {
      _subSectionTitle = moment(pickupTime).format(
        saleTypeHasFutureOrdering ? 'ddd, D MMM, YY h:mm a' : 'h:mm a',
      );
    }

    setSubSectionTitle(_subSectionTitle);
  }, [
    afterHoursOrdering,
    beforeOrdering,
    deliveryASAPEstimate,
    deliveryAddress,
    deliveryTime,
    deliveryTimeFetchedEstimateSyncing,
    isDelivery,
    isDeliveryTimeASAP,
    isPickupOrTakeAway,
    location.averageOrderWaitTime,
    LocationOT,
    pickupTime,
    renderDeliveryTimeASAPEstimateTimeSkeleton,
    saleType,
    saleTypeHasFutureOrdering,
    pickupOrTakeawayTimeIsAsap,
    t,
  ]);

  useEffect(() => {
    updateSubSectionTitle();
  }, [updateSubSectionTitle]);

  /**
   * ## showScheduled:
   *
   * When isPickupOrTakeAway:
   * -> show scheduled when the user hasn't picked asap
   *
   * When catering pickup:
   * -> always show scheduled
   */
  const [showScheduledWhenPickupOrTakeAway, setShowScheduledWhenPickupOrTakeAway] = useState(
    isPickupOrTakeAway && !pickupOrTakeawayTimeIsAsap,
  );
  const showScheduledAlwaysForSpecificSaletypes = [SALE_TYPE.CATERING].includes(saleType);
  const showScheduled =
    showScheduledWhenPickupOrTakeAway || showScheduledAlwaysForSpecificSaletypes;

  const [_showPickerForCurrentDateSelector, setShowPickerForCurrentDateSelector] = useState(false);

  const pickupTakeAwayOptions: SubSectionProps[] = [
    {
      title: t('asap'),
      subtitle: afterHoursOrdering
        ? moment(LocationOT)
            .add(location.averageOrderWaitTime, 'minutes')
            .add(beforeOrdering ? 0 : 1, 'day')
            .format(beforeOrdering ? 'h:mm a' : ' h:mm a DD/MM/YY ')
        : location.averageOrderWaitTime + ' mins',
      action: () => {
        setShowScheduledWhenPickupOrTakeAway(false);
        dispatch(setBufferPickupTime(null));
        dispatch(validateFutureMenuItems());
        dispatch(fetchOrderingProviderDetails({ bufferMode: false }));
      },
      rightSlot: () => <RadioCheck checked={!showScheduledWhenPickupOrTakeAway} size={35} />,
    },
    {
      title: t('title.scheduleForLater'),
      subtitle:
        showScheduledWhenPickupOrTakeAway && !saleTypeHasFutureOrdering
          ? moment(pickupTime).format('h:mm a')
          : t('title.chooseTime'),
      action: () => {
        setShowScheduledWhenPickupOrTakeAway(true);

        // If saletype doesn't have future ordering then show Current Date
        // Selector picker modal:
        if (!saleTypeHasFutureOrdering) {
          setShowPickerForCurrentDateSelector(true);
        }
      },
      rightSlot: () => <RadioCheck checked={showScheduledWhenPickupOrTakeAway} size={35} />,
    },
  ];

  return (
    <>
      <SectionHeading
        title={t('title.scheduledTime')}
        collapsed={optionsCollapsed}
        collapsedIconAction={() => setOptionsCollapsed(value => !value)}
      />
      <SectionContainer>
        {!optionsCollapsed && (
          <SubSection
            title={subSectionTitle}
            subtitle={deliveryTimeUnvailable ? 'Enter address before setting time' : undefined}
            action={() => {
              if (isDelivery) {
                if (deliveryTimeUnvailable) {
                  return;
                }
                dispatch(
                  setCurrentModal({
                    modalId: DELIVERY_DETAILS_MODAL_ID,
                    params: { allowRedirect: false, showOnlyTime: bypassDeliveryDetails, saleType },
                  }),
                );
              } else {
                setOptionsCollapsed(!optionsCollapsed);
              }
            }}
            rightSlot={() => (deliveryTimeUnvailable ? <></> : <SubSectionChevron />)}
          />
        )}

        {optionsCollapsed && (
          <div style={styles.options}>
            {isPickupOrTakeAway &&
              pickupTakeAwayOptions.map((option, i) => (
                <SubSection
                  key={i}
                  title={option.title}
                  subtitle={option.subtitle}
                  rightSlot={option.rightSlot}
                  action={option.action}
                />
              ))}

            {showScheduled && (
              <>
                {saleTypeHasFutureOrdering ? (
                  <FutureDateSelector
                    time={pickupTime}
                    setTime={time => {
                      dispatch(setBufferPickupTime(time));
                      logEvent(EVENTS.CHANGE_PICKUP_TIME);
                    }}
                  />
                ) : (
                  <CurrentDateSelector
                    time={pickupTime}
                    averageOrderWaitTime={location.averageOrderWaitTime}
                    earliestTime={location?.availableSaleTypesToday[String(saleType)].openingTime}
                    latestTime={location?.availableSaleTypesToday[String(saleType)].closingTime}
                    setTime={time => {
                      dispatch(setBufferPickupTime(time));
                    }}
                  />
                )}
              </>
            )}
          </div>
        )}
      </SectionContainer>
    </>
  );
};

export default TimeSummary;

const styles: Styles = {
  options: {
    paddingBottom: 24,
  },
};
